import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useLoanOfficerPermissionPreview', 'rbac');

interface RolePermissionRow {
  permission_id: string;
}

interface LoanOfficerPermissionPreview {
  isLoadingLoanOfficerPermissions: boolean;
  loanOfficerHasAnyPermission: (permissions: string[]) => boolean;
}

interface PermissionPreviewState {
  enabled: boolean;
  permissionIds: ReadonlySet<string>;
  status: 'idle' | 'loading' | 'ready' | 'error';
}

/**
 * Loads the enabled Loan Officer grant set used by Home's admin-only
 * customization audit. The caller controls when the query is enabled so the
 * ordinary sidebar and Home render path never adds a database request.
 */
export function useLoanOfficerPermissionPreview(
  enabled: boolean
): LoanOfficerPermissionPreview {
  const [previewState, setPreviewState] = useState<PermissionPreviewState>(() => ({
    enabled: false,
    permissionIds: new Set(),
    status: 'idle'
  }));
  const requestSequence = useRef(0);

  useEffect(() => {
    const requestId = ++requestSequence.current;

    if (!enabled) {
      setPreviewState({
        enabled: false,
        permissionIds: new Set(),
        status: 'idle'
      });
      return;
    }

    setPreviewState({
      enabled: true,
      permissionIds: new Set(),
      status: 'loading'
    });

    const loadPermissions = async () => {
      try {
        const { data, error } = await supabase
          .from('role_permissions')
          .select('permission_id')
          .eq('role', 'loan_officer')
          .eq('enabled', true);

        if (requestId !== requestSequence.current) {
          return;
        }

        if (error) {
          throw error;
        }

        const nextPermissionIds = new Set(
          ((data ?? []) as RolePermissionRow[])
            .map((row) => row.permission_id)
            .filter((permissionId): permissionId is string =>
              typeof permissionId === 'string' && permissionId.length > 0
            )
        );
        setPreviewState({
          enabled: true,
          permissionIds: nextPermissionIds,
          status: 'ready'
        });
      } catch (error) {
        if (requestId !== requestSequence.current) {
          return;
        }

        setPreviewState({
          enabled: true,
          permissionIds: new Set(),
          status: 'error'
        });
        const normalizedError = error instanceof Error
          ? error
          : new Error('Failed to load Loan Officer permission preview');
        log.error(
          'Failed to load Loan Officer permission preview',
          { operation: 'load-loan-officer-permissions' },
          normalizedError
        );
      }
    };

    void loadPermissions();

    return () => {
      if (requestSequence.current === requestId) {
        requestSequence.current += 1;
      }
    };
  }, [enabled]);

  const isPreviewReady = enabled
    && previewState.enabled
    && previewState.status === 'ready';
  const isLoadingLoanOfficerPermissions = enabled && !isPreviewReady;
  const loanOfficerHasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (permissions.length === 0) {
        return true;
      }
      if (!isPreviewReady) {
        return false;
      }
      return permissions.some((permission) =>
        previewState.permissionIds.has(permission)
      );
    },
    [isPreviewReady, previewState.permissionIds]
  );

  return {
    isLoadingLoanOfficerPermissions,
    loanOfficerHasAnyPermission
  };
}
